import React from "react";
import PropTypes from "prop-types";
import { formatCurrency, formatShownDate } from "./utils";
import { dte_names, frases } from "../../../services";
import dayjs from "dayjs";

export const InvoiceEmitTemplate = ({ invoice, extras = {}, visuals }) => {
	const today = dayjs(new Date());
	const { data } = invoice;
	const { xchange_rate = null, instructions = null } = extras;

	function isValidHexColor(color) {
		return /^#([A-Fa-f0-9]{3}){1,2}$/.test(color);
	}

	const defaultVisuals = {
		bg: "#bababa",
		border: "#bababa",
		additional: "#f9f9f9",
		text: "#000000",
	};
	
	// Use default colors if visuals is undefined or not an object
	const visualsObj = typeof visuals === 'object' && visuals !== null ? visuals : {};

	// Validate each color, use default if invalid
	const BG = isValidHexColor(visualsObj.bg) ? visualsObj.bg : defaultVisuals.bg;
	const BORDER = isValidHexColor(visualsObj.border) ? visualsObj.border : defaultVisuals.border;
	const ADDITIONAL = isValidHexColor(visualsObj.additional) ? visualsObj.additional : defaultVisuals.additional;
	const TEXT = isValidHexColor(visualsObj.text) ? visualsObj.text : defaultVisuals.text;

	function getRGB(hexColor) {
		const r = parseInt(hexColor.slice(1, 3), 16);
		const g = parseInt(hexColor.slice(3, 5), 16);
		const b = parseInt(hexColor.slice(5, 7), 16);

		return {r, g, b}
	}

	// Function to determine if a color is dark
	function isColorDark(hexColor) {
	// Convert hex to RGB
		const {r, g, b} = getRGB(hexColor);
		
		// Calculate luminance - standard formula for perceived brightness
		const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
		
		// Return true if dark (luminance less than 0.5)
		return luminance < 0.5;
	}

	// Determine header text color based on background darkness
	const headerTextColor = isColorDark(BG) ? "#FFFFFF" : TEXT;
	// Function to generate QR code URL (seems to be missing from original code)

	return (
		<html lang="es">
			<head>
				<meta charSet="UTF-8"/>
				<meta name="viewport" content="width=device-width, initial-scale=1.0"/>
				<title>Documento Tributario Electrónico</title>
				<style>
					{`
						/* estilos aquí */
          body {
            font-family: 'Arial', sans-serif;
            margin: 0;
            padding: 0;
            display: flex;
            flex-direction: column;
            min-height: 100vh;
            color: ${TEXT}
          }
          .container {
            flex: 1;
            width: auto;
            background-color: #ffffff;
            padding: 40px;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
			color: ${TEXT};
          }
          .header, .footer {
            color: ${TEXT};
            display: flex;
            justify-content: space-between;
			color: ${TEXT};
          }
          .logo-container {
            display: flex;
            flex-direction: column;
            align-items: center;
			color: ${TEXT};
          }
          .header img {
            height: 80px;
            object-fit: contain;
			color: ${TEXT};
          }
          .logo-text {
            margin-top: 5px;
            font-size: 12px;
            text-align: center;
			color: ${TEXT};
          }
          .invoice-details {
            display: flex;
            justify-content: space-between;
            padding-bottom: 20px;
            padding-top: 20px;
			color: ${TEXT};
          }
          .company-info, .invoice-info {
            width: 45%;
			color: ${TEXT};
          }
          .company-info h3, .company-info h5, .company-info h4,
          .invoice-info h3, .invoice-info h5, .invoice-info h4 {
            margin: 0;
            line-height: 1.2;
			color: ${TEXT};
          }
          .invoice-details p {
            margin: 5px 0 0 0;
            font-size: 13px;
			color: ${TEXT};
          }
          .invoice-info {
            text-align: right;
            display: flex;
            flex-direction: column;
            align-items: flex-end;
			color: ${TEXT};
          }
          .table {
            width: 100%;
            border-collapse: collapse;
			color: ${TEXT};
          }
          .table thead {
            background-color: ${BG};
            color: ${headerTextColor};
          }
          .table th, .table td {
            padding: 4px;
            border-bottom: 2px solid ${BORDER};
            vertical-align: top;
			color: ${TEXT};
          }
          .table th {
            color: ${headerTextColor};
            text-align: left;
            border-bottom: 3px solid ${BORDER};
            border-top: 3px solid ${BORDER};
            background-color: ${BG};
          }
          .table th.description { width: 40%; }
          .table th.quantity { width: 10%; }
          .table th.unit-price, .table th.discount, .table th.total {
            width: 15%;
            text-align: right;
			color: ${TEXT};
          }
          .table td { font-size: 13px; }
          .table td.amount { text-align: right; }
          .taxes { color: gray; font-size: 11px; }
          .total { text-align: right; margin-top: 20px; }
          .total h2 { margin: 0; }
          .footer { font-size: 10px; text-align: center; }
          .footer .certifier { font-weight: bold; }
          .watermark {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) rotate(-30deg);
            font-size: 5em;
            color: rgba(255, 0, 0, 0.2);
            text-transform: uppercase;
            letter-spacing: 10px;
            white-space: nowrap;
            z-index: 0;
            pointer-events: none;
          }
          /* Estilos para el bloque adicional */
          .additional-info {
            margin-top: 20px;
            padding: 15px;
            background-color: #FFFFFF;
            border: 1px solid ${BORDER};
            border-radius: 5px;
            font-size: 13px;
			color: ${TEXT};
          }
          .additional-info p {
            margin: 0;
          }
          .instructions-title {
            font-weight: bold;
            color: ${TEXT};
            margin-bottom: 5px;
            display: block;
          }
          .instructions-text {
            white-space: pre-wrap;
            word-break: break-word;
            display: block;
            margin-top: 5px;
            padding-left: 10px;
			color: ${TEXT};
          }
          .additional-info strong {
            color: ${TEXT};
          }
					`}
				</style>
			</head>
			<body>
				<div className="container">
					<div>
						<div className="header">
							<h1>{dte_names[invoice.data.type]}</h1>
							{visuals.logo_url && (
								<div className="logo-container">
									<img src={visuals.logo_url} alt="Logo" style={{height: "80px"}} />
								</div>
							)}
						</div>
						<div className="invoice-details">
							<div className="company-info">
								<h4>{invoice.data.sender.name}</h4>
								<p>{invoice.data.sender.tid}</p>
								<p>{invoice.data.sender.address}</p>
							</div>
							<div className="invoice-info">
								<h4>Fecha de Emisión: ...</h4>
								<p>Moneda: {invoice.data.currency}</p>
							</div>
						</div>
						<div className="invoice-details">
							<div className="company-info">
								<h4>Datos del Cliente:</h4>
								<p>{invoice.data.receptor.name}</p>
								<p>{invoice.data.receptor.tid}</p>
								<p>{invoice.data.receptor.address}</p>
							</div>
							<div className="invoice-info">
								<p>Número de Certificación</p>
								<p>Serie: ...</p>
								<p>Número: ...</p>
							</div>
						</div>
						<table className="table">
							<thead>
								<tr>
									<th className="description">Descripción</th>
									<th className="quantity">Cantidad</th>
									<th className="unit-price">Precio Unitario</th>
									<th className="discount">Descuento</th>
									<th className="total">Total</th>
								</tr>
							</thead>
							<tbody>
								{invoice.data.items && invoice.data.items.map((item, index) => (
									<tr key={index}>
										<td>{item.initial_description || item.description}</td>
										<td className="amount">{item.quantity}</td>
										<td className="amount">{formatCurrency(item.priceUnit, invoice.data.currency)}</td>
										<td className="amount">{formatCurrency(item.discount || 0, invoice.data.currency)}</td>
										<td className="amount">
											{formatCurrency(item.total || (item.quantity * item.priceUnit - (item.discount || 0)), invoice.data.currency)}
											{item.taxes && item.taxes.map((tax, taxIndex) => (
												<div key={taxIndex} className="taxes">
													[ {tax.name} {formatCurrency(tax.total, invoice.data.currency)} ]
												</div>
											))}
										</td>
									</tr>
								))}
							</tbody>
						</table>
						<div className="total">
							<h2>
								Total: {" "}
								{formatCurrency(data?.items?.reduce(
									(sum, item) => sum + item.quantity * item.priceUnit - (item.discount || 0), 0
								), data?.currency)}
							</h2>
						</div>
						<div className="invoice-details">
							<div className="company-info">
								{invoice.data.frases && invoice.data.frases.map((frase, index) => (
									<p key={index}>*{frases[frase.type][frase.scenario]}</p>
								))}
							</div>
						</div>
						{(xchange_rate || instructions) && (
							<div className="additional-info">
								{xchange_rate && (
									<p><strong>Tipo de Cambio:</strong> {typeof xchange_rate === "string" ? xchange_rate : formatCurrency(xchange_rate, data?.currency)}</p>
								)}
								{instructions && (
									<div>
										<span className="instructions-title">Instrucciones:</span>
										<span className="instructions-text">{instructions}</span>
									</div>
								)}
							</div>
						)}
					</div>
				</div>
			</body>
		</html>
	);
};

InvoiceEmitTemplate.propTypes = {
	invoice: PropTypes.shape({
		data: PropTypes.shape({
			sender: PropTypes.shape({
				name: PropTypes.string,
				address: PropTypes.string,
				tid: PropTypes.string,
			}),
			receptor: PropTypes.shape({
				name: PropTypes.string,
				address: PropTypes.string,
				tid: PropTypes.string,
			}),
			date: PropTypes.string,
			type: PropTypes.string,
			currency: PropTypes.string,
			items: PropTypes.arrayOf(
				PropTypes.shape({
					description: PropTypes.string,
					quantity: PropTypes.number,
					priceUnit: PropTypes.number,
					discount: PropTypes.number,
				})
			),
			frases: PropTypes.arrayOf(
				PropTypes.shape({
					type: PropTypes.string,
					scenario: PropTypes.string,
				})
			),
		}).isRequired,
	}).isRequired,
	extras: PropTypes.object,
	visuals: PropTypes.object,
};
